/**
 * Validation helpers shared by every schema in this package.
 *
 * All imported data (config, fixture, scene map, layouts, commands, state messages)
 * is parsed through zod schemas. These helpers turn zod issues into one aggregated,
 * human-readable report so a bad file fails loudly with every problem listed at once.
 */
import type { z } from 'zod';

/** One validation problem, located by a dotted path into the input. */
export interface ValidationIssue {
  /** Dotted path into the input, e.g. `slots.2.boundsWidth`. `(root)` for the input itself. */
  path: string;
  /** Human-readable explanation. */
  message: string;
}

/** Thrown by {@link parseOrThrow}. Carries every issue, not just the first. */
export class ValidationError extends Error {
  /** What was being validated, e.g. `layout file "4-box.json"`. */
  readonly context: string;
  readonly issues: readonly ValidationIssue[];

  constructor(context: string, issues: readonly ValidationIssue[]) {
    super(`Invalid ${context}:\n${issues.map((i) => `  - ${i.path}: ${i.message}`).join('\n')}`);
    this.name = 'ValidationError';
    this.context = context;
    this.issues = issues;
  }
}

/** Result of {@link safeParseWith}: the typed value, or every issue found. */
export type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; issues: ValidationIssue[] };

/** Converts zod issues into {@link ValidationIssue}s. */
export function toIssues(error: z.core.$ZodError): ValidationIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.length === 0 ? '(root)' : issue.path.map(String).join('.'),
    message: issue.message,
  }));
}

/** Parses `data` with `schema`; returns every issue instead of throwing. */
export function safeParseWith<S extends z.ZodType>(
  schema: S,
  data: unknown,
): ParseResult<z.output<S>> {
  const result = schema.safeParse(data);
  return result.success ? { ok: true, value: result.data } : { ok: false, issues: toIssues(result.error) };
}

/**
 * Parses `data` with `schema` or throws a {@link ValidationError} listing every issue.
 * @param context Names the input in the error message, e.g. `config file "config.json"`.
 */
export function parseOrThrow<S extends z.ZodType>(schema: S, data: unknown, context: string): z.output<S> {
  const result = safeParseWith(schema, data);
  if (!result.ok) throw new ValidationError(context, result.issues);
  return result.value;
}

/**
 * Returns the values that occur more than once in `values`, each reported once,
 * in first-seen order. Used by the uniqueness refinements.
 */
export function findDuplicates<T>(values: Iterable<T>): T[] {
  const seen = new Set<T>();
  const dupes = new Set<T>();
  for (const v of values) {
    if (seen.has(v)) dupes.add(v);
    seen.add(v);
  }
  return [...dupes];
}
